import { useStepperContext } from "app/contexts/stepper";
import { useInscripcionContext } from "../../inscripcion-context";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useState } from "react";
import { toast } from "react-hot-toast";
import { useResourcesContext } from "app/contexts/resources";
import {
  celularRegex,
  correoElectronicoRegex,
  identificacionRegex,
  nombreCompletoRegex,
  rtnRegex,
  telefonoRegex,
} from "app/utils/regexps";
import { deleteSpaces } from "app/utils";

const initialState = {
  genero_id: 0,
  encargado_nombre_completo: "",
  encargado_identificacion: "",
  encargado_rtn: "",
  encargado_telefono: "",
  encargado_celular: "",
  encargado_correo_electronico: "",
  cargos: [],
};

function EncargadoForm({ encargado = initialState }) {
  const { generos, cargos } = useResourcesContext();

  const { addEncargado, updateEncargado, deleteEncargado } =
    useInscripcionContext();

  const [state, setState] = useState(encargado);
  const [isEditable] = useState(!!encargado.encargado_id);
  const [isEditing, setEditing] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();

    try {
      if (!!isEditable && !isEditing) {
        return setEditing(true);
      } else if (!!isEditable && !!isEditing) {
        const isUpdated = updateEncargado({
          encargado: state,
        });

        if (isUpdated) {
          setEditing(false);

          return toast.success("¡Encargado actualizado!");
        }
      } else {
        const isAdded = addEncargado({ encargado: state });

        if (isAdded) {
          setState(encargado);

          return toast.success("¡Encargado agregado!");
        }
      }
    } catch (error) {
      console.log(error.message);

      return toast.error(error.message);
    }
  }

  function handleReset() {
    if (!!isEditable && !isEditing) {
      const isDeleted = deleteEncargado({
        encargado_id: encargado.encargado_id,
      });

      if (isDeleted) {
        return toast.success("¡Encargado eliminado!");
      }
    } else if (!!isEditable && !!isEditing) {
      const isCanceled = window.confirm("¿Desea cancelar la edición?");

      if (isCanceled) {
        setState(encargado);

        return setEditing(false);
      }
    } else {
      const isReseted = window.confirm("¿Desea reiniciar el formulario?");

      if (isReseted) {
        setState(encargado);
      }
    }
  }

  return (
    <Paper
      component="form"
      className="animate__animated animate__slideInUp"
      onSubmit={handleSubmit}
      onReset={handleReset}
      variant="outlined"
    >
      <Grid container spacing={1} sx={{ padding: 2 }}>
        <Grid item xs={12}>
          <Typography variant="overline">
            <strong>{`${
              !!isEditable ? "Editar" : "Agregar"
            } encargado`}</strong>
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControl
            size="small"
            fullWidth
            required
            disabled={!!isEditable && !isEditing}
          >
            <InputLabel id="cargos-label">Cargos</InputLabel>

            <Select
              labelId="cargos-label"
              multiple
              value={state.cargos || []}
              onChange={(e) =>
                setState((encargado) => ({
                  ...encargado,
                  cargos: e.target.value,
                }))
              }
              input={<OutlinedInput label="Chip" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((cargo_id) => {
                    const cargo = cargos.results.find(
                      (result) => result.cargo_id === cargo_id
                    );

                    return <Chip key={cargo_id} label={cargo.cargo_nombre} />;
                  })}
                </Box>
              )}
            >
              {cargos.results.map((result) => (
                <MenuItem key={result.cargo_id} value={result.cargo_id}>
                  <Checkbox
                    checked={
                      !!state.cargos.find(
                        (cargo_id) => cargo_id === result.cargo_id
                      )
                    }
                  />

                  <ListItemText primary={result.cargo_nombre} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid container item xs={12} md={6} spacing={1}>
          <Grid item xs={12}>
            <FormControl
              size="small"
              fullWidth
              required
              disabled={!!isEditable && !isEditing}
            >
              <InputLabel id="departamento-label">Género</InputLabel>

              <Select
                label="Género"
                labelId="departamento-label"
                value={state.genero_id || ""}
                onChange={(e) =>
                  setState((encargado) => ({
                    ...encargado,
                    genero_id: e.target.value,
                  }))
                }
              >
                {generos.results.map((genero) => (
                  <MenuItem key={genero.genero_id} value={genero.genero_id}>
                    {genero.genero_nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              type="text"
              label="Nombre completo"
              value={state.encargado_nombre_completo || ""}
              onChange={(e) =>
                setState((state) => ({
                  ...state,
                  encargado_nombre_completo: e.target.value,
                }))
              }
              inputProps={nombreCompletoRegex}
              size="small"
              fullWidth
              required
              disabled={!!isEditable && !isEditing}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              type="text"
              label="Núm. de identidad, pasaporte o carnet de residente"
              value={state.encargado_identificacion || ""}
              onChange={(e) =>
                setState((state) => ({
                  ...state,
                  encargado_identificacion: deleteSpaces(e.target.value),
                }))
              }
              inputProps={identificacionRegex}
              size="small"
              fullWidth
              required
              disabled={!!isEditable && !isEditing}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              type="text"
              label="Número de RTN"
              value={state.encargado_rtn || ""}
              inputProps={rtnRegex}
              onChange={(e) =>
                setState((state) => ({
                  ...state,
                  encargado_rtn: deleteSpaces(e.target.value),
                }))
              }
              size="small"
              fullWidth
              required
              disabled={!!isEditable && !isEditing}
            />
          </Grid>
        </Grid>

        <Grid container item xs={12} md={6} spacing={1}>
          <Grid item xs={12}>
            <TextField
              type="text"
              label="Número de teléfono (opcional)"
              value={state.encargado_telefono || ""}
              onChange={(e) =>
                setState((state) => ({
                  ...state,
                  encargado_telefono: deleteSpaces(e.target.value),
                }))
              }
              inputProps={telefonoRegex}
              size="small"
              fullWidth
              disabled={!!isEditable && !isEditing}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              type="text"
              label="Número de celular"
              value={state.encargado_celular || ""}
              onChange={(e) =>
                setState((state) => ({
                  ...state,
                  encargado_celular: deleteSpaces(e.target.value),
                }))
              }
              inputProps={celularRegex}
              size="small"
              fullWidth
              required
              disabled={!!isEditable && !isEditing}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              type="text"
              label="Dirección de correo electrónico (opcional)"
              value={state.encargado_correo_electronico || ""}
              onChange={(e) =>
                setState((encargado) => ({
                  ...encargado,
                  encargado_correo_electronico: deleteSpaces(e.target.value),
                }))
              }
              inputProps={correoElectronicoRegex}
              size="small"
              fullWidth
              disabled={!!isEditable && !isEditing}
            />
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" justifyContent="end" spacing={1}>
              <Button
                type="submit"
                variant="contained"
                size="small"
                color={!!isEditable ? "warning" : "success"}
              >
                {!!isEditable && !isEditing
                  ? "Editar"
                  : !!isEditing
                  ? "Actualizar"
                  : "Agregar"}
              </Button>

              <Button
                type="reset"
                variant="outlined"
                size="small"
                color={!!isEditable && !isEditing ? "error" : "inherit"}
              >
                {!!isEditable && !isEditing
                  ? "Eliminar"
                  : !!isEditing
                  ? "Cancelar"
                  : "Reiniciar"}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}

export function Encargados() {
  const stepper = useStepperContext();
  const { empresa } = useInscripcionContext();

  function handleNext() {
    try {
      if (!empresa.encargados.length) {
        throw new Error("¡Por favor, agregue al menos un encargado!");
      }

      return stepper.next();
    } catch (error) {
      return toast.error(error.message);
    }
  }

  return (
    <Stack spacing={2}>
      <Container maxWidth="md">
        <Stack spacing={2}>
          {empresa.encargados.map((encargado) => (
            <EncargadoForm key={encargado.encargado_id} encargado={encargado} />
          ))}

          <EncargadoForm />
        </Stack>
      </Container>

      <Stack
        direction="row"
        justifyContent="end"
        alignItems="center"
        spacing={1}
      >
        {!stepper.isFirstStep && (
          <Button variant="outlined" size="small" onClick={stepper.back}>
            Atrás
          </Button>
        )}

        <Button variant="contained" size="small" onClick={handleNext}>
          Siguiente
        </Button>
      </Stack>
    </Stack>
  );
}
